import { useEffect } from 'react';
import { useRouter } from '@funtech-int/rival-react';
import { NextEpisode } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useFeatureFlags } from '@funtech-int/yango-shared';

import { QueryParameter } from '@/shared/constants/queryParams';
import { playingContentIdVar } from '@/scenarios/legacy-player/components/player-manager/hooks/use-playing-content-id-var';
import { removeQueryParamsFromUrl } from '@/shared/lib/navigation';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';
import { useSearchParams } from '@/shared/hooks/useSearchParams';

interface AutoStartMovieProps {
    isMoviePurchased: boolean;
    filmId: string;
    nextEpisode: NextEpisode | null;
}

export const AutoStartMovie = ({ isMoviePurchased, filmId, nextEpisode }: AutoStartMovieProps) => {
    const router = useRouter();
    const { flags } = useFeatureFlags();
    const isNewPlayerEnabled = flags.get('isNewPlayerEnabled', 'boolean', false);
    const searchParams = useSearchParams();

    useEffect(() => {
        const isWatch = searchParams.has(QueryParameter.WATCH);
        const isMovieModal = searchParams.has(QueryParameter.REDIRECT_TO_FILM_PAGE);

        if (!isWatch || isMovieModal) {
            return;
        }

        const hasPlayingContent = playingContentIdVar.get();

        if (!isMoviePurchased) {
            removeQueryParamsFromUrl({ router, queryParams: [QueryParameter.WATCH] });
            return;
        }

        if (nextEpisode?.contentId) {
            if (nextEpisode.contentId && !hasPlayingContent) {
                startPlaying({
                    router,
                    seriesInfo: {
                        seasonNumber: nextEpisode.seasonNumber ?? 0,
                        episodeNumber: nextEpisode.episodeNumber,
                    },
                    contentId: nextEpisode.contentId,
                    contentType: PlayerContentType.EPISODE,
                    isNewPlayerEnabled,
                });
            }
            return;
        }

        if (filmId) {
            if (!hasPlayingContent) {
                startPlaying({
                    router,
                    contentId: filmId,
                    contentType: PlayerContentType.FILM,
                    isNewPlayerEnabled,
                });
            }
            return;
        }

        removeQueryParamsFromUrl({
            router,
            queryParams: [
                QueryParameter.WATCH,
                QueryParameter.SEASON_NUMBER,
                QueryParameter.EPISODE_NUMBER,
                QueryParameter.PLAYING_CONTENT_ID,
            ],
        });
    }, [
        router,
        searchParams,
        filmId,
        isMoviePurchased,
        isNewPlayerEnabled,
        nextEpisode?.contentId,
        nextEpisode?.seasonNumber,
        nextEpisode?.episodeNumber,
    ]);

    return null;
};
